import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import config from "../config";
import useTranslation from './useTranslation';
import authService from '../services/auth';
import { calculateDistance } from './utils';
import TinyPoiCardList from './TinyPoiCardList';
import LocalIcon from './LocalIcon';

const Search = ({ 
  isOpen, 
  isAnimating, 
  onClose, 
  initialSearchTerm = '' 
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [allPois, setAllPois] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxonomyData, setTaxonomyData] = useState({});
  const [userLocation, setUserLocation] = useState(null);
  const [sortOption, setSortOption] = useState('alphabetical');
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const sortDropdownRef = useRef(null);
  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);

  const apiUrl = config.API_ENDPOINTS.getPOIList(selectedLang);

  // Effetto per caricamento dati
  useEffect(() => {
    const fetchData = async () => {
      try {
        // All'inizio del caricamento, imposta loading a true
        setLoading(true);
        // Reset dello stato di ricerca
        setSearchPerformed(false);
        
        const response = await authService.get(apiUrl);
        
        // Adattamento per gestire sia il vecchio formato (response.data) che il nuovo (response)
        const poisData = Array.isArray(response) ? response : (response.data || []);
        
        // Ordina i POI alfabeticamente prima di salvarli
        const sortedPois = [...poisData].sort((a, b) => 
          a.title[0].value.localeCompare(b.title[0].value)
        );
        setAllPois(sortedPois);

        const taxonomyRequests = sortedPois.map(poi => {
          if (poi.field_tipologia_poi && poi.field_tipologia_poi.length > 0) {
            return authService.get(config.API_ENDPOINTS.getTaxonomyTerm(poi.field_tipologia_poi[0].target_id, selectedLang));
          }
          return null;
        });

        const taxonomyResponses = await Promise.all(taxonomyRequests.filter(req => req !== null));
        const taxonomyInfo = {};
        taxonomyResponses.forEach(response => {
          const term = response.data || response;
          taxonomyInfo[term.tid[0].value] = {
            title: term.name[0].value,
            icon: term.field_icona ? term.field_icona[0]?.url : null
          };
        });
        setTaxonomyData(taxonomyInfo);

        // Initial search con i POI già ordinati
        if (initialSearchTerm) {
          // Non chiamiamo fetchResults, ma filtriamo direttamente qui
          // per evitare cambi di stato troppo rapidi
          const filtered = filterPois(sortedPois, initialSearchTerm);
          setSearchResults(filtered);
          setSearchPerformed(true); // Ricerca iniziale completata
        }

        // Solo ora impostiamo loading a false, dopo aver completato anche la ricerca iniziale
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
      fetchUserLocation();
    } else {
      // Reset dei risultati quando il pannello viene chiuso
      setSearchResults([]);
      setSearchPerformed(false);
    }
  }, [isOpen, initialSearchTerm, apiUrl, selectedLang]);

  // Gestione scroll body
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const fetchUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
        },
        (error) => console.error('Error getting location:', error)
      );
    }
  };

  // Sort results when sort option changes
  useEffect(() => {
    if (searchResults.length === 0) return;
    
    const sorted = [...searchResults].sort((a, b) => {
      if (sortOption === 'distance' && userLocation) {
        const latLngA = a.field_geolocalizzazione?.[0];
        const latLngB = b.field_geolocalizzazione?.[0];
        
        const distanceA = latLngA 
          ? calculateDistance(userLocation.latitude, userLocation.longitude, latLngA.lat, latLngA.lng) 
          : Infinity;
        
        const distanceB = latLngB 
          ? calculateDistance(userLocation.latitude, userLocation.longitude, latLngB.lat, latLngB.lng) 
          : Infinity;
        
        return distanceA - distanceB;
      }
      
      // Default to alphabetical
      return a.title[0].value.localeCompare(b.title[0].value);
    });
    
    setSearchResults(sorted);
  }, [sortOption, userLocation]);

  const filterPois = (pois, searchQuery) => {
    if (!searchQuery) return [];
    
    const filtered = pois.filter(poi => {
      const title = poi.title[0]?.value?.toLowerCase() || '';
      const description = poi.field_descrizione_breve[0]?.value?.toLowerCase() || '';
      const fullDescription = poi.body[0]?.value?.toLowerCase() || '';
      const address = poi.field_indirizzo?.[0]?.value?.toLowerCase() || '';
      
      const searchTerms = searchQuery.toLowerCase().split(' ');
      
      return searchTerms.some(term => 
        title.includes(term) ||
        description.includes(term) ||
        fullDescription.includes(term) ||
        address.includes(term)
      );
    });

    // Mantieni l'ordinamento alfabetico quando filtri
    return filtered.sort((a, b) => 
      a.title[0].value.localeCompare(b.title[0].value)
    );
  };

  // Click outside per i dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Classi per il pannello con animazioni
  const panelClasses = [
    'focus-panel',
    isOpen ? 'is-open' : '',
    isAnimating ? 'is-closing' : ''
  ].join(' ');

  // Se il pannello non è aperto e non sta animando, non renderizzarlo
  if (!isOpen && !isAnimating) return null;

  const fetchResults = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchPerformed(false);
      return;
    }

    try {
      // Imposta lo stato di caricamento all'inizio della ricerca
      setLoading(true);
      
      // Utilizzo il filtraggio locale invece di una chiamata API
      const filtered = filterPois(allPois, query);
      
      // Aggiungiamo un minimo ritardo per mostrare lo stato di caricamento
      // e per evitare che l'interfaccia "salti" troppo velocemente
      setTimeout(() => {
        setSearchResults(filtered);
        setSearchPerformed(true); // Ricerca completata
        setLoading(false);
      }, 300);
    } catch (error) {
      console.error('Errore nella ricerca:', error);
      setSearchResults([]);
      setSearchPerformed(true); // Ricerca completata (con errore)
      setLoading(false);
    }
  };

  return (
    <div className={panelClasses}>
      {/* Header */}
      <div className="sticky-top bg-white border-bottom">
        <div className="container d-flex justify-content-between align-items-center py-3">
          <button
            onClick={onClose}
            aria-label={t('chiudi')}
            className="btn bg-white d-flex align-items-center justify-content-center goback"
            style={{ position: 'relative', top: 0, left: 0 }}
          >
            <LocalIcon icon="close" />
          </button>
          <LocalIcon icon="search" />
        </div>
      </div>

      {/* Resto del componente */}
      <div className="container py-4">
        {/* Titolo e descrizione */}
        <div className="mb-4">
          <h2>{t('risultatiRicerca')}</h2>
          {initialSearchTerm && <p>{t('risultatiTrovati')}: "{initialSearchTerm}"</p>}
        </div>

        {/* Dropdown Ordinamento - Aggiornato per matching con ListPanel */}
        <div className="d-flex justify-content-end mb-4">
          <div className="dropdown" ref={sortDropdownRef}>
            <button 
              className="btn border rounded-pill dropdown-toggle"
              type="button"
              onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
              style={{
                backgroundColor: 'var(--color-element)',
                color: 'var(--color-text-primary)'
              }}
            >
              {t('ordinamento')}
            </button>
            {isSortDropdownOpen && (
              <ul className="dropdown-menu show mt-1" style={{ 
                width: '230px', 
                right: '0',
                backgroundColor: 'var(--color-surface)',
                borderColor: 'var(--color-border)'
              }}>
                <li 
                  className="d-flex align-items-center p-2 rounded"
                  onClick={() => {
                    setSortOption('alphabetical');
                    setIsSortDropdownOpen(false);
                  }}
                  style={{ 
                    cursor: 'pointer',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  <span className="flex-grow-1">{t('alfabetico')}</span>
                  {sortOption === 'alphabetical' && (
                    <LocalIcon 
                      icon="check" 
                      style={{ 
                        fontSize: '18px', 
                        color: 'var(--color-text-base)' 
                      }}
                    />
                  )}
                </li>
                <li 
                  className="d-flex align-items-center p-2 rounded"
                  onClick={() => {
                    setSortOption('distance');
                    setIsSortDropdownOpen(false);
                  }}
                  style={{ 
                    cursor: 'pointer',
                    color: 'var(--color-text-base)' 
                  }}
                >
                  <span className="flex-grow-1">{t('distanza')}</span>
                  {sortOption === 'distance' && (
                    <LocalIcon 
                      icon="check" 
                      style={{ 
                        fontSize: '18px', 
                        color: 'var(--color-text-base)' 
                      }}
                    />
                  )}
                </li>
              </ul>
            )}
          </div>
        </div>

        {/* Lista risultati */}
        <section>
          {loading ? (
            <div className="m-3">{t('stoCercando')}</div>
          ) : searchPerformed && searchResults.length === 0 ? (
            <div className="m-3">{t('nessunRisultato')}</div>
          ) : !searchPerformed ? (
            <div className="m-3">{t('cercaPlaceholder')}</div>
          ) : (
            <ul className="list-group">
              {searchResults.map((poi) => {
                const distance = userLocation && poi.field_geolocalizzazione?.[0]
                  ? calculateDistance(
                      userLocation.latitude,
                      userLocation.longitude,
                      poi.field_geolocalizzazione[0].lat,
                      poi.field_geolocalizzazione[0].lng
                    ).toFixed(1) + ' km'
                  : '';

                return (
                  <li key={poi.nid[0].value} className="list-group-item list-group-item-divider mt-3 pt-3">
                    <TinyPoiCardList
                      poi={poi}
                      taxonomyData={taxonomyData}
                      distance={distance}
                      onClick={() => {
                        navigate(`/poi/${poi.nid[0].value}`);
                        onClose(); // Chiude il pannello di ricerca
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default Search;